/**
 * PreferencesTab — /settings/preferences
 * Theme (next-themes, this device), then the saved preferences form. Demo page: /profile (account form + notification
 * toggles). Template example: NextAdmin `profile/account/page.tsx`, `profile/notification/page.tsx`.
 *
 * The theme is not part of the form: it is owned by next-themes and applies at once, so the header toggle and this
 * setting always agree (both call `setTheme`).
 */
import React from 'react';
import { useTheme } from 'next-themes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { getMeQueryKey, updateMeMutation } from '../../generated/api/@tanstack/react-query.gen.js';
import type { GetMeResponse } from '../../generated/api/types.gen.js';
import { zUserProfileUpdate } from '../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  FormSection,
  NumberFormField,
  SelectFormField,
  SwitchFormField,
  useZodForm,
} from '../../components/form/index.js';
import { FieldLabel } from '../../components/ui/FieldLabel.js';
import { Select } from '../../components/ui/Input.js';
import { useToast } from '../../components/ui/Toast.js';

const TIMEZONES = [
  'UTC',
  'US/Eastern',
  'US/Central',
  'US/Mountain',
  'US/Pacific',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Australia/Sydney',
].map((value) => ({ value, label: value.replace('_', ' ') }));

const EXCHANGES = [
  { value: 'binance', label: 'Binance' },
  { value: 'bybit', label: 'Bybit' },
];

const THEMES = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'Match system' },
];

const settingsSchema = zUserProfileUpdate.shape.settings
  .unwrap()
  .pick({ timezone: true, defaultExchange: true, defaultLeverage: true, notifications: true })
  .required();

interface PreferencesTabProps {
  user: GetMeResponse['user'];
}

export const PreferencesTab: React.FC<PreferencesTabProps> = ({ user }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const { theme, setTheme } = useTheme();
  const save = useMutation(updateMeMutation());
  const saved = user.settings;
  const form = useZodForm(settingsSchema, {
    defaultValues: {
      timezone: saved?.timezone ?? 'UTC',
      defaultExchange: saved?.defaultExchange ?? 'binance',
      defaultLeverage: saved?.defaultLeverage ?? 1,
      notifications: saved?.notifications ?? true,
    },
  });

  const submit = async (settings: z.output<typeof settingsSchema>) => {
    await save.mutateAsync({ body: { settings } });
    await queryClient.invalidateQueries({ queryKey: getMeQueryKey() });
    notify('Preferences saved.');
  };

  return (
    <div className="space-y-8">
      <section>
        <h2 className="mb-1 text-xl leading-7 font-semibold text-heading">Appearance</h2>
        <p className="mb-6 text-sm text-muted">Applies on this device right away.</p>
        <div className="max-w-sm">
          <FieldLabel label="Theme" htmlFor="theme-select" />
          <Select id="theme-select" value={theme ?? 'system'} onChange={(event) => setTheme(event.target.value)}>
            {THEMES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>
      </section>

      <Form form={form} onSubmit={submit} guardUnsaved>
        <FormSection title="Defaults" description="Used when a form asks for a time zone or an exchange.">
          <SelectFormField name="timezone" label="Time zone" options={TIMEZONES} />
          <SelectFormField name="defaultExchange" label="Default exchange" options={EXCHANGES} />
          <NumberFormField
            name="defaultLeverage"
            label="Default leverage"
            minValue={1}
            maxValue={125}
            description="From 1 to 125."
          />
          <SwitchFormField
            name="notifications"
            label="Send me notifications"
            description="Turn off to stop all alerts to your account."
          />
          <FormActions submitLabel="Save preferences" onCancel={() => form.reset()} />
        </FormSection>
      </Form>
    </div>
  );
};
